import { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { IEmpresas } from '../../interfaces/IEmpresas';
import PlanoContaEmpresaService, {
    formatarData,
    IPlanoContaEmpresa,
    IPlanoContaEmpresaFiltro,
    IPlanoContaEmpresaForm,
    IPlanoContaEventoOpcao,
} from '../../services/PlanoContaEmpresaService';
import PlanoContaHistoricoModal from './PlanoContaHistoricoModal';

/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

// DEVN3-466 — aba "Plano de Contas" do cadastro da empresa: o cliente mantém as contas da empresa
// (código, nome, conta referencial = evento, início de vigência) e ativa/inativa. As regras e as
// mensagens de erro vêm da API (400 { message }); aqui só a checagem mínima antes de enviar.

export const ABA_PLANO_CONTAS = '5';
const POR_PAGINA = 20;
const filtroInicial: IPlanoContaEmpresaFiltro = { codigo: '', nome: '', referencial: '', situacao: 'ATIVAS' };
const formVazio: IPlanoContaEmpresaForm = { codigo: '', nome: '', eventoId: null, dataInicioVigencia: '' };

type Opcao = { value: number; label: string };

const rotuloEvento = (contaReferencial: string, nome?: string | null) =>
    `${contaReferencial}${nome ? ` – ${nome}` : ''}`;

function PlanoContasModal({ empresa, abaSelecionada }: { empresa: IEmpresas; abaSelecionada: string }) {
    const { t } = useTranslation();
    const ativa = abaSelecionada === ABA_PLANO_CONTAS;

    const [itens, setItens] = useState<IPlanoContaEmpresa[]>([]);
    const [total, setTotal] = useState(0);
    const [pagina, setPagina] = useState(0);
    const [filtro, setFiltro] = useState<IPlanoContaEmpresaFiltro>(filtroInicial);
    const [filtroAplicado, setFiltroAplicado] = useState<IPlanoContaEmpresaFiltro>(filtroInicial);
    const [loading, setLoading] = useState(false);
    const [eventos, setEventos] = useState<IPlanoContaEventoOpcao[]>([]);

    const [form, setForm] = useState<IPlanoContaEmpresaForm | null>(null);
    const [editando, setEditando] = useState<IPlanoContaEmpresa | null>(null);
    const [salvando, setSalvando] = useState(false);
    const [alterandoSituacao, setAlterandoSituacao] = useState<number | null>(null);
    const [contaHistorico, setContaHistorico] = useState<IPlanoContaEmpresa | null>(null);

    const erro = useCallback((e: any, padrao: string) =>
        Swal.fire({ text: e?.message || t(padrao), icon: 'error', timer: 6000, showConfirmButton: false }), [t]);

    const carregar = useCallback(async () => {
        if (!empresa?.id) return;
        setLoading(true);
        try {
            const r = await PlanoContaEmpresaService.listar(empresa.id, filtroAplicado, pagina * POR_PAGINA, POR_PAGINA);
            setItens(r.itens || []);
            setTotal(r.total || 0);
        } catch (e: any) {
            setItens([]);
            setTotal(0);
            erro(e, 'Não foi possível carregar o plano de contas.');
        } finally {
            setLoading(false);
        }
    }, [empresa?.id, filtroAplicado, pagina, erro]);

    useEffect(() => {
        if (ativa) carregar();
    }, [ativa, carregar]);

    // Catálogo de eventos ativos: carrega uma vez, quando a aba abre pela primeira vez.
    useEffect(() => {
        if (!ativa || !empresa?.id || eventos.length > 0) return;
        PlanoContaEmpresaService.eventos(empresa.id)
            .then(setEventos)
            .catch((e: any) => erro(e, 'Não foi possível carregar as contas referenciais.'));
    }, [ativa, empresa?.id, eventos.length, erro]);

    // Filtros com debounce — voltam para a primeira página.
    useEffect(() => {
        const timer = setTimeout(() => {
            setPagina(0);
            setFiltroAplicado({ ...filtro, codigo: filtro.codigo.trim(), nome: filtro.nome.trim(), referencial: filtro.referencial.trim() });
        }, 400);
        return () => clearTimeout(timer);
    }, [filtro]);

    const opcoesEventos = useMemo<Opcao[]>(() => {
        const opcoes = eventos.map(e => ({ value: e.id, label: rotuloEvento(e.contaReferencial, e.nome) }));
        // Conta com evento que foi inativado depois: o evento atual continua selecionável (só ele).
        if (editando && !editando.eventoAtivo && !opcoes.some(o => o.value === editando.eventoID)) {
            opcoes.unshift({
                value: editando.eventoID,
                label: `${rotuloEvento(editando.contaReferencial, editando.eventoNome)} (${t('inativo')})`,
            });
        }
        return opcoes;
    }, [eventos, editando, t]);

    const abrirNova = () => {
        setEditando(null);
        setForm({ ...formVazio });
    };

    const abrirEdicao = (item: IPlanoContaEmpresa) => {
        setEditando(item);
        setForm({
            codigo: item.codigo,
            nome: item.nome,
            eventoId: item.eventoID,
            dataInicioVigencia: item.dataInicioVigencia ? item.dataInicioVigencia.substring(0, 10) : '',
        });
    };

    const fecharForm = () => {
        setForm(null);
        setEditando(null);
    };

    const salvar = async () => {
        if (!form || !empresa?.id) return;
        if (!form.codigo.trim() || !form.nome.trim() || !form.eventoId) {
            Swal.fire({ text: t('Informe o código, o nome e a conta referencial.'), icon: 'warning', timer: 3000, showConfirmButton: false });
            return;
        }
        setSalvando(true);
        try {
            if (editando) {
                await PlanoContaEmpresaService.atualizar(empresa.id, editando.id, form);
            } else {
                await PlanoContaEmpresaService.criar(empresa.id, form);
            }
            fecharForm();
            Swal.fire({ text: editando ? t('Conta atualizada.') : t('Conta incluída no plano de contas.'), icon: 'success', timer: 2000, showConfirmButton: false });
            await carregar();
        } catch (e: any) {
            erro(e, 'Não foi possível salvar a conta.');
        } finally {
            setSalvando(false);
        }
    };

    const alternarSituacao = async (item: IPlanoContaEmpresa) => {
        if (!empresa?.id) return;
        const acao = item.ativo ? t('Inativar') : t('Ativar');
        const confirmacao = await Swal.fire({
            text: `${acao} ${t('a conta')} ${item.codigo} — ${item.nome}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: acao,
            cancelButtonText: t('Cancelar'),
            confirmButtonColor: item.ativo ? '#EF4444' : '#00B7A1',
        });
        if (!confirmacao.isConfirmed) return;

        setAlterandoSituacao(item.id);
        try {
            if (item.ativo) await PlanoContaEmpresaService.inativar(empresa.id, item.id);
            else await PlanoContaEmpresaService.ativar(empresa.id, item.id);
            // Saiu do filtro atual e era a última da página: volta uma página.
            if (filtroAplicado.situacao !== 'TODAS' && itens.length === 1 && pagina > 0) setPagina(pagina - 1);
            else await carregar();
        } catch (e: any) {
            erro(e, 'Não foi possível alterar a situação da conta.');
        } finally {
            setAlterandoSituacao(null);
        }
    };

    const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));
    const filtrando = !!(filtroAplicado.codigo || filtroAplicado.nome || filtroAplicado.referencial);

    return (
        <div style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '15px', minHeight: '505px', display: ativa ? 'block' : 'none' }}>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-4">
                <div>
                    <h4 className="fw-bolder text-dark m-0" style={{ fontSize: '16px' }}>{t('Plano de Contas')}</h4>
                    <div className="text-muted mt-1" style={{ fontSize: '12px' }}>
                        {t('Contas da empresa com a conta referencial. Contas não são excluídas: inative as que saírem de uso.')}
                    </div>
                </div>
                <button className="btn btn-sm" style={{ backgroundColor: '#00B7A1', color: '#fff', height: '38px' }} onClick={abrirNova}>
                    {t('Nova conta')}
                </button>
            </div>

            <div className="row g-2 mb-4">
                <div className="col-md-3">
                    <input type="text" className="form-control form-control-sm" style={{ height: '38px' }} placeholder={t('Código contábil')}
                        value={filtro.codigo} onChange={e => setFiltro({ ...filtro, codigo: e.target.value })} />
                </div>
                <div className="col-md-3">
                    <input type="text" className="form-control form-control-sm" style={{ height: '38px' }} placeholder={t('Nome da conta')}
                        value={filtro.nome} onChange={e => setFiltro({ ...filtro, nome: e.target.value })} />
                </div>
                <div className="col-md-4">
                    <input type="text" className="form-control form-control-sm" style={{ height: '38px' }} placeholder={t('Conta referencial (código ou nome)')}
                        value={filtro.referencial} onChange={e => setFiltro({ ...filtro, referencial: e.target.value })} />
                </div>
                <div className="col-md-2">
                    <select className="form-select form-select-sm" style={{ height: '38px' }} value={filtro.situacao}
                        onChange={e => setFiltro({ ...filtro, situacao: e.target.value as IPlanoContaEmpresaFiltro['situacao'] })}>
                        <option value="ATIVAS">{t('Ativas')}</option>
                        <option value="INATIVAS">{t('Inativas')}</option>
                        <option value="TODAS">{t('Todas')}</option>
                    </select>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-row-bordered align-middle gs-3 gy-2 mb-0">
                    <thead>
                        <tr className="fw-bold text-muted" style={{ fontSize: '12px' }}>
                            <th style={{ width: '140px' }}>{t('Código')}</th>
                            <th>{t('Nome da conta')}</th>
                            <th style={{ width: '260px' }}>{t('Conta referencial')}</th>
                            <th style={{ width: '110px' }}>{t('Início vigência')}</th>
                            <th style={{ width: '90px' }}>{t('Situação')}</th>
                            <th style={{ width: '130px' }} className="text-end">{t('Ações')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="text-center text-muted py-5">{t('Carregando...')}</td></tr>
                        ) : itens.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="text-center text-muted py-5">
                                    {filtrando || filtroAplicado.situacao !== 'ATIVAS'
                                        ? t('Nenhuma conta encontrada para os filtros.')
                                        : t('Nenhuma conta cadastrada. Clique em "Nova conta" para começar.')}
                                </td>
                            </tr>
                        ) : itens.map(item => (
                            <tr key={item.id} style={{ fontSize: '13px', opacity: item.ativo ? 1 : 0.65 }}>
                                <td className="fw-bold text-dark">{item.codigo}</td>
                                <td>{item.nome}</td>
                                <td>
                                    {rotuloEvento(item.contaReferencial, item.eventoNome)}
                                    {!item.eventoAtivo && (
                                        <span className="badge badge-light-warning ms-2" title={t('Esta conta referencial foi inativada. A conta continua vinculada; ao editar, você pode mantê-la ou trocar por uma ativa.')}>
                                            {t('evento inativo')}
                                        </span>
                                    )}
                                </td>
                                <td>{formatarData(item.dataInicioVigencia) || '—'}</td>
                                <td>
                                    <span className={`badge ${item.ativo ? 'badge-light-success' : 'badge-light-danger'}`}>
                                        {item.ativo ? t('Ativa') : t('Inativa')}
                                    </span>
                                </td>
                                <td className="text-end text-nowrap">
                                    <button className="btn btn-icon btn-sm btn-light me-1" title={t('Editar')} onClick={() => abrirEdicao(item)}>
                                        <i className="bi bi-pencil"></i>
                                    </button>
                                    <button className={`btn btn-icon btn-sm me-1 ${item.ativo ? 'btn-light-danger' : 'btn-light-success'}`}
                                        title={item.ativo ? t('Inativar') : t('Ativar')}
                                        disabled={alterandoSituacao === item.id}
                                        onClick={() => alternarSituacao(item)}>
                                        <i className={`bi ${item.ativo ? 'bi-slash-circle' : 'bi-check-circle'}`}></i>
                                    </button>
                                    <button className="btn btn-icon btn-sm btn-light" title={t('Histórico')} onClick={() => setContaHistorico(item)}>
                                        <i className="bi bi-clock-history"></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-4">
                <span className="text-muted" style={{ fontSize: '12px' }}>{total} {t('conta(s)')}</span>
                <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-light" disabled={pagina === 0 || loading} onClick={() => setPagina(pagina - 1)}>{t('Anterior')}</button>
                    <span className="text-muted" style={{ fontSize: '12px' }}>{t('Página')} {pagina + 1} {t('de')} {totalPaginas}</span>
                    <button className="btn btn-sm btn-light" disabled={pagina + 1 >= totalPaginas || loading} onClick={() => setPagina(pagina + 1)}>{t('Próxima')}</button>
                </div>
            </div>

            {form && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(15, 23, 42, 0.4)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }} onClick={fecharForm}>
                    <div className="card shadow-lg" style={{ width: '520px', maxWidth: '92vw', borderRadius: '16px', border: 'none' }} onClick={e => e.stopPropagation()}>
                        <div className="card-header d-flex justify-content-between align-items-center py-4 px-5" style={{ background: '#F8FAFC', borderRadius: '16px 16px 0 0' }}>
                            <h3 className="card-title fw-bolder text-dark m-0" style={{ fontSize: '18px' }}>{editando ? t('Editar conta') : t('Nova conta')}</h3>
                            <button className="btn btn-icon btn-sm btn-active-light-primary" onClick={fecharForm}>
                                <i className="bi bi-x fs-2"></i>
                            </button>
                        </div>
                        <div className="card-body p-5">
                            <div className="mb-4">
                                <label className="form-label fw-bold fs-7 required">{t('Código contábil')}</label>
                                <input type="text" className="form-control" maxLength={50} autoFocus
                                    value={form.codigo} onChange={e => setForm({ ...form, codigo: e.target.value })} />
                            </div>
                            <div className="mb-4">
                                <label className="form-label fw-bold fs-7 required">{t('Nome da conta')}</label>
                                <input type="text" className="form-control" maxLength={255}
                                    value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} />
                            </div>
                            <div className="mb-4">
                                <label className="form-label fw-bold fs-7 required">{t('Conta referencial')}</label>
                                <Select
                                    options={opcoesEventos}
                                    value={opcoesEventos.find(o => o.value === form.eventoId) || null}
                                    onChange={(o: Opcao | null) => setForm({ ...form, eventoId: o?.value ?? null })}
                                    placeholder={t('Selecione a conta referencial')}
                                    noOptionsMessage={() => t('Nenhuma conta referencial encontrada')}
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 10000 }) }}
                                />
                            </div>
                            <div>
                                <label className="form-label fw-bold fs-7">{t('Início de vigência')}</label>
                                <input type="date" className="form-control"
                                    value={form.dataInicioVigencia} onChange={e => setForm({ ...form, dataInicioVigencia: e.target.value })} />
                                <div className="text-muted mt-1" style={{ fontSize: '11px' }}>{t('Opcional.')}</div>
                            </div>
                        </div>
                        <div className="card-footer d-flex justify-content-end gap-2 py-4 px-5" style={{ borderRadius: '0 0 16px 16px' }}>
                            <button className="btn btn-sm btn-secondary px-4" onClick={fecharForm} disabled={salvando}>{t('Cancelar')}</button>
                            <button className="btn btn-sm px-4" style={{ backgroundColor: '#00B7A1', color: '#fff' }} onClick={salvar} disabled={salvando}>
                                {salvando ? t('Salvando...') : t('Salvar')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {contaHistorico && empresa?.id && (
                <PlanoContaHistoricoModal empresaId={empresa.id} conta={contaHistorico} onFechar={() => setContaHistorico(null)} />
            )}
        </div>
    );
}

export default PlanoContasModal;
